import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CustomWatchlistReportService, { CustomReportAlumniRow, CustomReportResponse } from './CustomWatchlistReportService';
import CustomWatchlistReportTable from './CustomWatchlistReportTable';
import UpdateStageModal from './UpdateStageModal';
import RemoveMemberModal from './RemoveMemberModal';
import { CHIP_DIMENSIONS } from './labelMaps';
import { STAGE_LABELS_CUSTOM } from './customStage';
import { allowPerformAction } from '../../../library/Core/SmartFunctions';

const STAGE_FILTER_OPTIONS = ['identified', 'validated', 'warmed', 'engaged', 'qualified', 'contribution', 'dormant', 'denied'] as const;

/**
 * Custom Watchlist Alumni Report — listing view for members of a single custom watchlist.
 * Opens from a dashboard card's "View All →" link with pre-filled URL params.
 */
const CustomWatchlistReportPage = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const instituteId = params.get('institute_id');
    const watchlistId = params.get('watchlist_id');
    const filterInstituteId = params.get('filter_institute_id');
    const canMutate = allowPerformAction();

    const [data, setData] = useState<CustomReportResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [programs, setPrograms] = useState<string[]>([]);
    const [batchYears, setBatchYears] = useState<number[]>([]);
    const [exporting, setExporting] = useState(false);
    const [stageModalRow, setStageModalRow] = useState<CustomReportAlumniRow | null>(null);
    const [removeModalRow, setRemoveModalRow] = useState<CustomReportAlumniRow | null>(null);
    const [actionMessage, setActionMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!instituteId || !watchlistId) return;
        CustomWatchlistReportService.getFilterOptions(instituteId, watchlistId, filterInstituteId)
            .then((r) => { setPrograms(r.programs || []); setBatchYears(r.batch_years || []); })
            .catch(() => { setPrograms([]); setBatchYears([]); });
    }, [instituteId, watchlistId, filterInstituteId]);

    const filters = useMemo(() => {
        const f: Record<string, any> = {};
        params.forEach((v, k) => { f[k] = v; });
        return f;
    }, [params]);

    const fetchData = useCallback(() => {
        if (!instituteId) { setError('No institute selected.'); setLoading(false); return; }
        if (!watchlistId) { setError('watchlist_id is required in the URL.'); setLoading(false); return; }
        setLoading(true);
        setError(null);
        CustomWatchlistReportService.getReport(filters as any)
            .then(setData)
            .catch((e) => setError(e?.response?.data?.error?.message || e?.message || 'Failed to load report.'))
            .finally(() => setLoading(false));
    }, [filters, instituteId, watchlistId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setFilter = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        if (value === '' || value == null) next.delete(key);
        else next.set(key, value);
        if (key !== 'page') next.delete('page');
        setParams(next);
    };

    const clearAll = () => {
        const next = new URLSearchParams();
        if (instituteId) next.set('institute_id', instituteId);
        if (watchlistId) next.set('watchlist_id', watchlistId);
        const fi = params.get('filter_institute_id');
        if (fi) next.set('filter_institute_id', fi);
        setParams(next);
    };

    const activeChips = CHIP_DIMENSIONS.filter((d) => params.get(d.key));

    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await CustomWatchlistReportService.exportCSV(filters as any);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `custom-watchlist-report-${watchlistId}-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || e?.message || 'Export failed.');
        } finally {
            setExporting(false);
        }
    };

    const handleUpload = () => {
        if (!instituteId || !watchlistId) return;
        const q = new URLSearchParams();
        q.set('category', 'WATCHLIST');
        q.set('open_upload', '1');
        q.set('watchlist_id', watchlistId);
        q.set('institute_id', instituteId);
        if (filterInstituteId) q.set('filter_institute_id', filterInstituteId);
        try { sessionStorage.setItem('activityLogAutoOpen', '1'); } catch (_) { /* ignore */ }
        navigate(`/management/track/activity-log-report?${q.toString()}`, { state: { openUpload: true } });
    };

    const onStageDone = () => {
        setStageModalRow(null);
        setActionMessage('Stage updated.');
        fetchData();
    };

    const onRemoveDone = () => {
        setRemoveModalRow(null);
        setActionMessage('Member removed.');
        fetchData();
    };

    const page = data?.page ?? 1;
    const totalPages = data?.total_pages ?? 1;

    if (!instituteId || !watchlistId) {
        return (
            <div className="main p-4">
                <div className="alert alert-danger">institute_id and watchlist_id are required in the URL.</div>
            </div>
        );
    }

    const dashboardPath = filterInstituteId
        ? `/support/watchlist-dashboard/${instituteId}?filter_institute_id=${filterInstituteId}`
        : `/support/watchlist-dashboard/${instituteId}`;

    return (
        <div className="main p-3 p-md-4">
            <div className="max-1140 mx-auto">
                <div className="mb-2">
                    <Link to={dashboardPath} className="font-12 text-primary text-decoration-none">
                        <i className="bi bi-arrow-left me-1"></i>Back to Watchlist Dashboard
                    </Link>
                </div>

                <div className="d-flex align-items-center justify-content-between mb-2">
                    <div>
                        <h2 className="m-0 font-22 font-600 text-dark-blue">
                            {data?.watchlist?.name || 'Custom Watchlist Report'}
                        </h2>
                        <p className="m-0 font-13 text-secondary-emphasis">
                            {data ? `${data.total_count} alumni` : '…'} matching the current filters
                            {data?.watchlist?.tag && (
                                <span className="ms-2 badge bg-success bg-opacity-10 text-success font-11">{data.watchlist.tag}</span>
                            )}
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        {canMutate && (
                            <button type="button" className="btn btn-outline-success rounded-3 font-13" onClick={handleUpload}>
                                Upload Engagement Activity
                            </button>
                        )}
                        <button className="btn btn-outline-primary rounded-3 font-13" onClick={handleExport} disabled={exporting}>
                            {exporting ? 'Exporting…' : '⬇ Export CSV'}
                        </button>
                    </div>
                </div>

                <div className="bg-white border rounded-3 p-3 mb-3 shadow-sm">
                    <div className="row g-2">
                        <div className="col-md-3">
                            <input
                                className="form-control form-control-sm rounded-3 font-13"
                                placeholder="Search name…"
                                defaultValue={params.get('search') || ''}
                                onKeyDown={(e) => { if (e.key === 'Enter') setFilter('search', (e.target as HTMLInputElement).value); }}
                            />
                        </div>
                        <div className="col-md-2">
                            <select className="form-select form-select-sm rounded-3 font-13" value={params.get('program') || ''} onChange={(e) => setFilter('program', e.target.value)}>
                                <option value="">All programs</option>
                                {programs.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select form-select-sm rounded-3 font-13" value={params.get('batch_year') || ''} onChange={(e) => setFilter('batch_year', e.target.value)}>
                                <option value="">All batch years</option>
                                {batchYears.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select form-select-sm rounded-3 font-13" value={params.get('alma_mater_pride') || ''} onChange={(e) => setFilter('alma_mater_pride', e.target.value)}>
                                <option value="">Alma Mater Pride: any</option>
                                <option value="HIGH">High</option>
                                <option value="MODERATE">Moderate</option>
                                <option value="LOW">Low</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select form-select-sm rounded-3 font-13" value={params.get('marketability') || ''} onChange={(e) => setFilter('marketability', e.target.value)}>
                                <option value="">Marketability: any</option>
                                <option value="HIGH">High</option>
                                <option value="MODERATE">Moderate</option>
                                <option value="LOW">Low</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select form-select-sm rounded-3 font-13" value={params.get('vibrancy') || ''} onChange={(e) => setFilter('vibrancy', e.target.value)}>
                                <option value="">Vibrancy: any</option>
                                <option value="HIGH">High</option>
                                <option value="MODERATE">Moderate</option>
                                <option value="LOW">Low</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <input
                                type="date"
                                className="form-control form-control-sm rounded-3 font-13"
                                value={params.get('date_from') || ''}
                                onChange={(e) => setFilter('date_from', e.target.value)}
                                title="Added from"
                            />
                        </div>
                        <div className="col-md-2">
                            <input
                                type="date"
                                className="form-control form-control-sm rounded-3 font-13"
                                value={params.get('date_to') || ''}
                                onChange={(e) => setFilter('date_to', e.target.value)}
                                title="Added to"
                            />
                        </div>
                        <div className="col-md-2">
                            <select className="form-select form-select-sm rounded-3 font-13" value={params.get('sort') || 'ability_desc'} onChange={(e) => setFilter('sort', e.target.value)}>
                                <option value="ability_desc">Ability ↓</option>
                                <option value="ability_asc">Ability ↑</option>
                                <option value="name_asc">Name A–Z</option>
                                <option value="date_desc">Newest added</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select form-select-sm rounded-3 font-13" value={params.get('stage') || ''} onChange={(e) => setFilter('stage', e.target.value)}>
                                <option value="">All stages</option>
                                {STAGE_FILTER_OPTIONS.map((code) => (
                                    <option key={code} value={code}>{STAGE_LABELS_CUSTOM[code]}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {activeChips.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                        {activeChips.map((d) => {
                            const raw = params.get(d.key) as string;
                            return (
                                <span key={d.key} className="badge bg-primary bg-opacity-10 text-primary font-11 d-inline-flex align-items-center gap-1">
                                    {d.label}: {d.format ? d.format(raw) : raw}
                                    <i className="bi bi-x cursor-pointer" onClick={() => setFilter(d.key, '')}></i>
                                </span>
                            );
                        })}
                        <button className="btn btn-sm btn-link text-decoration-none font-11 p-0" onClick={clearAll}>Clear All Filters</button>
                    </div>
                )}

                {actionMessage && <div className="alert alert-success font-13">{actionMessage}</div>}
                {error && <div className="alert alert-danger font-13">{error}</div>}
                {loading ? (
                    <div className="font-14 text-secondary-emphasis py-4">Loading report…</div>
                ) : data && (
                    <>
                        <CustomWatchlistReportTable
                            rows={data.alumni}
                            onUpdateStage={setStageModalRow}
                            onRemove={setRemoveModalRow}
                        />
                        <div className="d-flex align-items-center justify-content-between mt-3">
                            <span className="font-12 text-muted">Page {page} of {totalPages} · {data.total_count} total</span>
                            <div className="d-flex gap-2">
                                <button className="btn btn-sm btn-outline-secondary rounded-3" disabled={page <= 1} onClick={() => setFilter('page', String(page - 1))}>← Prev</button>
                                <button className="btn btn-sm btn-outline-secondary rounded-3" disabled={page >= totalPages} onClick={() => setFilter('page', String(page + 1))}>Next →</button>
                            </div>
                        </div>
                    </>
                )}
                {stageModalRow && instituteId && watchlistId && (
                    <UpdateStageModal
                        row={stageModalRow}
                        watchlistId={watchlistId}
                        instituteId={instituteId}
                        filterInstituteId={filterInstituteId}
                        onClose={() => setStageModalRow(null)}
                        onDone={onStageDone}
                    />
                )}
                {removeModalRow && instituteId && watchlistId && (
                    <RemoveMemberModal
                        row={removeModalRow}
                        watchlistId={watchlistId}
                        watchlistName={data?.watchlist?.name}
                        instituteId={instituteId}
                        filterInstituteId={filterInstituteId}
                        onClose={() => setRemoveModalRow(null)}
                        onDone={onRemoveDone}
                    />
                )}
            </div>
        </div>
    );
};

export default CustomWatchlistReportPage;
